/**
 * Central query-key factory for TanStack Query.
 *
 * WHY THIS EXISTS: a query key is both the cache's primary key AND the unit of
 * invalidation. Ad-hoc inline arrays (`['brand-kit', projectId]`) scattered
 * across pages drift — one typo in a mutation's `invalidateQueries` and the
 * refetch silently no-ops, leaving stale data on screen with no error. Defining
 * every key ONCE here makes reads and their matching invalidations impossible
 * to misspell.
 *
 * CONVENTION
 *  - Keys are hierarchical tuples, broadest segment first, so a broad
 *    invalidation cascades to everything nested under it. Invalidating
 *    `qk.projectAll(id)` matches every per-project query that starts with
 *    `['project', id]`.
 *  - Every factory returns `as const` so the tuple type is preserved.
 *  - The values here intentionally match the raw arrays already used by
 *    existing React-Query components (e.g. `['brand-kit', projectId]`,
 *    `['project', projectId]`) so adopting the factory never orphans a cache
 *    entry or breaks an existing invalidation.
 *
 * If you need a key that isn't here, add it here (don't inline it at the call
 * site) so the next person can find and invalidate it.
 */
export const qk = {
  // ── Global / dashboard ──────────────────────────────────────────────────
  projects: () => ['projects'] as const,

  // ── Per-project (all share the ['project', id] prefix for cascade) ───────
  /** Broad root — invalidate to refetch everything scoped to one project. */
  projectAll: (projectId?: string) => ['project', projectId] as const,
  project: (projectId?: string) => ['project', projectId] as const,
  // Telemetry: `telemetry` is the cascade root — invalidating it refreshes both
  // the group list (every status filter) and the header stats in one call.
  telemetry: (projectId?: string) => ['telemetry', projectId] as const,
  telemetryGroups: (projectId: string | undefined, status: string) =>
    ['telemetry', projectId, 'groups', status] as const,
  telemetryStats: (projectId?: string) => ['telemetry', projectId, 'stats'] as const,
  brandKit: (projectId?: string) => ['brand-kit', projectId] as const,
  // Search engines card (Google + Bing lifecycle + IndexNow). The card polls
  // this while an engine is `verifying`; the dashboard reads it to gate the
  // Search Console reports — one key, so both always agree.
  seoEngines: (projectId?: string) => ['seo-engines', projectId] as const,
  // The project's ownership tokens (manual fallback fields under Advanced).
  seoTokens: (projectId?: string) => ['project-seo', projectId] as const,
  // AI Answer Visibility. `aeo` is the cascade root: "Run now" and a setup
  // save invalidate it so summary, grid and drill-downs all refresh together.
  aeo: (projectId?: string) => ['aeo', projectId] as const,
  aeoSummary: (projectId?: string) => ['aeo', projectId, 'summary'] as const,
  aeoGrid: (projectId?: string) => ['aeo', projectId, 'grid'] as const,
  aeoContent: (projectId?: string) => ['aeo', projectId, 'content'] as const,
  aeoProbes: (projectId: string | undefined, questionId: string, engine: string) =>
    ['aeo', projectId, 'probes', questionId, engine] as const,
  products: (projectId?: string) => ['products', projectId] as const,
  product: (projectId: string | undefined, productId: string) =>
    ['products', projectId, productId] as const,
  // NOTE: value is 'ecommerce-settings' (not 'commerce-settings') — it must
  // match the de-facto key already used by CategoryManagerDialog,
  // BulkImportDialog, Orders, etc., or their cross-invalidations silently no-op.
  commerceSettings: (projectId?: string) => ['ecommerce-settings', projectId] as const,
  ecommerceCategories: (projectId?: string) => ['ecommerce-categories', projectId] as const,
  // NOTE: value is 'blog-categories' — it must match the de-facto key already
  // used by Blog.tsx and BlogPostEditor.tsx, or the category manager's
  // invalidations won't refresh the pickers those pages render.
  blogCategories: (projectId?: string) => ['blog-categories', projectId] as const,
  blogPosts: (projectId?: string) => ['blog-posts', projectId] as const,
  ecommerceDashboard: (projectId?: string) => ['ecommerce-dashboard', projectId] as const,
  projectUsage: (projectId?: string) => ['project-usage', projectId] as const,
  generationJobs: (projectId?: string) => ['generation-jobs-full', projectId] as const,
  // A single run. Deliberately NOT nested under `generationJobs`: the editor
  // polls this key every 3s while a build runs, and sharing a prefix would drag
  // the whole Versions list into every one of those refetches. The trade is
  // that a job reaching a terminal state must invalidate `generationJobs`
  // explicitly — WebsiteEditor does. NOTE: value is 'generator-job' to match
  // the de-facto key this page already used, so no cache entry is orphaned.
  generationJob: (projectId?: string, jobId?: string) =>
    ['generator-job', projectId, jobId] as const,
  storefrontSpec: () => ['storefront-spec'] as const,
  chatbot: (projectId?: string) => ['chatbot', projectId] as const,
  chatbotBusinessInfo: (projectId?: string) => ['chatbot-business-info', projectId] as const,
  pageOwnership: (projectId?: string) => ['page-ownership', projectId] as const,
  projectPlugins: (projectId?: string) => ['project-plugins', projectId] as const,
  // YOM-backed analytics. `analytics` is the cascade root; status decides
  // which page renders, value cards are keyed by the range they describe.
  analyticsStatus: (projectId?: string) => ['analytics', projectId, 'status'] as const,
  analyticsValues: (projectId: string | undefined, from: string, to: string, compare: boolean) =>
    ['analytics', projectId, 'values', from, to, compare] as const,
  analyticsRealtime: (projectId?: string) => ['analytics', projectId, 'realtime'] as const,
  /** "Has this site ever had a visit?" (first-run state). */
  analyticsEver: (projectId?: string) => ['analytics', projectId, 'ever'] as const,
  // NOTE: value is 'plugin-manifest' — it must match the de-facto key already
  // used by PluginSettings, PluginBilling, and OAuthPropertyPicker (which
  // invalidate/setQueryData against it), or their cache writes go to a dead key.
  pluginDetail: (projectId: string | undefined, pluginId: string) =>
    ['plugin-manifest', projectId, pluginId] as const,
  dnsMigration: (projectId?: string) => ['dns-migration', projectId] as const,
  // Website Studio (agent runs). `studio` is the cascade root for a project's
  // overview + run list; a single run has its own key so the live view can
  // refetch it without re-pulling history.
  studio: (projectId?: string) => ['studio', projectId] as const,
  studioRun: (projectId: string | undefined, runId: string | undefined) =>
    ['studio', projectId, 'run', runId] as const,
  // The team's first project — the studio front door (/website) resolves it.
  myProject: () => ['studio', 'my-project'] as const,

  // ── Team / account settings ─────────────────────────────────────────────
  team: (teamId?: string) => ['team', teamId] as const,
  teamMembers: (teamId?: string) => ['team-members', teamId] as const,
  teamBilling: (teamId?: string) => ['team-billing', teamId] as const,
  // /billing/invoices is implicitly scoped to the caller's team, so the
  // differentiator is the page number.
  invoices: (page?: number | string) => ['invoices', page] as const,
  pricing: (teamId?: string) => ['pricing', teamId] as const,
  // AI credits (Website Studio wallet) — team-scoped server-side.
  aiCredits: () => ['ai-credits'] as const,
  aiCreditLedger: () => ['ai-credits', 'ledger'] as const,

  // ── Superadmin ──────────────────────────────────────────────────────────
  adminTeams: () => ['admin', 'teams'] as const,
  offlinePayments: () => ['admin', 'offline-payments'] as const,
  vouchers: () => ['admin', 'vouchers'] as const,
  paymentLinks: () => ['admin', 'payment-links'] as const,
  costMonitor: (range?: string) => ['admin', 'cost-monitor', range] as const,
  sre: () => ['admin', 'sre'] as const,
  platformInsightsPulse: (from: string, to: string) =>
    ['admin', 'platform-insights', 'pulse', from, to] as const,
  /** Prefix for every KPI range (invalidate this after a recompute). */
  platformKpisAll: () => ['admin', 'platform-insights', 'kpis'] as const,
  platformKpis: (from: string, to: string) =>
    ['admin', 'platform-insights', 'kpis', from, to] as const,
  sreDependencies: () => ['admin', 'sre', 'dependencies'] as const,
  sreCronSummary: () => ['admin', 'sre', 'cron-summary'] as const,
  sreCronJobs: () => ['admin', 'sre', 'cron-jobs'] as const,
  /**
   * CERT-In subscriber register. `subscriberRegister()` is the cascade root:
   * a save or backfill invalidates it so the list and the open record refresh
   * together.
   */
  subscriberRegister: () => ['admin', 'subscriber-register'] as const,
  subscriberRegisterList: (filters: {
    q: string;
    status: string;
    incomplete: boolean;
    page: number;
  }) => ['admin', 'subscriber-register', 'list', filters] as const,
  subscriberRegisterRecord: (teamId: string) =>
    ['admin', 'subscriber-register', 'record', teamId] as const,
  /**
   * Blast radius of deleting a user. Deliberately NOT under the ['users']
   * prefix: the delete dialog refetches this on open, and sharing a prefix
   * would drag the whole user list into that refetch.
   */
  userDeletionImpact: (userId: string) => ['admin', 'user-deletion-impact', userId] as const,

  // ── Account: quarterly rules notice text (status rides the user object) ──
  userRulesNotice: () => ['user-rules-notice'] as const,

  // ── Account: tools signed in with a personal access token ───────────────
  cliTokens: () => ['cli-tokens'] as const,
  /** A pending device sign-in request (the /device approval page). */
  deviceRequest: (userCode: string) => ['device-request', userCode] as const,

  // ── Testimonials plugin (root key cascades to list/stats/invites) ───────
  testimonials: (projectId?: string) => ['testimonials', projectId] as const,
  testimonialList: (projectId: string | undefined, status: string) =>
    ['testimonials', projectId, 'list', status] as const,
  testimonialStats: (projectId?: string) => ['testimonials', projectId, 'stats'] as const,
  testimonialInvites: (projectId?: string) => ['testimonials', projectId, 'invites'] as const,

  // ── Public (token-scoped, unauthenticated) ──────────────────────────────
  paymentLink: (token?: string) => ['pay', token] as const,
} as const;
